import { TablePaginationConfig } from 'antd';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { Column, RecordCountBadge, Switch, Table } from 'components';
import {
  StyledTableHeader,
  StyledTableWrapper,
} from 'components/table/table.styled';
import { getUserFullName, usePermissions } from 'features/users';
import { EndUserFilterFormValuesPaginated } from 'features/users/components/users-filter-form';
import { useUpdateUser } from 'features/users/use-cases/update-user';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import { getRoute } from 'routes/utils';
import { User } from 'types';
import { DEFAULT_TIMEZONE, formatDate } from 'utils/date';

type UsersTableProps = {
  isUsersListLoading: boolean;
  usersList: User[];
  itemsCount: number;
  tableParams: EndUserFilterFormValuesPaginated;
  updateParams: Dispatch<SetStateAction<EndUserFilterFormValuesPaginated>>;
};

export const EndUsersTable = ({
  usersList,
  isUsersListLoading,
  itemsCount,
  tableParams,
  updateParams,
}: UsersTableProps) => {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const navigate = useNavigate();

  const { mutate: mutateUpdateUser, isLoading: isLoadingUpdateUser } =
    useUpdateUser();

  if (!permissions) return null;

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<User> | SorterResult<User>[],
  ) => {
    updateParams({
      ...tableParams,
      page: pagination.current,
      size: pagination.pageSize,
      order_by: Array.isArray(sorter)
        ? undefined
        : sorter.order === 'ascend'
        ? 'name'
        : '-name',
    });
  };

  return (
    <StyledTableWrapper>
      <StyledTableHeader>
        <RecordCountBadge
          text={t('adminUsers.foundUsers')}
          count={itemsCount}
        />
      </StyledTableHeader>
      <Table<User>
        loading={isUsersListLoading}
        dataSource={usersList}
        rowKey='id'
        onChange={handleTableChange}
        pagination={{
          current: tableParams.page,
          defaultPageSize: tableParams.size,
          total: itemsCount,
        }}
        scroll={{ x: 1440 }}
        onRow={(record) => {
          return {
            onClick: () => {
              const selection = window?.getSelection()?.toString();

              if (!selection?.length) {
                navigate(getRoute(ROUTES.USERS_APP_EDIT, record.id));
              }
            },
          };
        }}
      >
        <Column<User>
          title={t('adminUsers.table.customerName')}
          dataIndex={'customerName'}
          render={(_, record) => getUserFullName(record)}
          sorter={true}
          sortDirections={['ascend', 'descend', 'ascend']}
          defaultSortOrder={'ascend'}
        />
        <Column<User>
          title={t('adminUsers.table.email')}
          dataIndex={'user'}
          render={(_, record) => record.email}
        />
        <Column<User>
          title={t('adminUsers.table.phoneNumber')}
          dataIndex={'user'}
          render={(_, record) => record.phone_number}
        />
        <Column<User>
          title={t('adminUsers.table.date')}
          dataIndex={'user'}
          render={(_, record) =>
            formatDate({
              date: record.created_at,
              format: 'date',
              timeZone: DEFAULT_TIMEZONE,
            })
          }
        />
        <Column<User>
          title={t('adminUsers.table.enableDisable')}
          dataIndex={'user'}
          render={(_, record) => (
            <Switch
              defaultChecked={record.is_active}
              checked={record.is_active}
              loading={isLoadingUpdateUser}
              onChange={(isChecked) =>
                mutateUpdateUser({
                  id: record.id,
                  is_active: isChecked,
                })
              }
              onClick={(checked, event) => {
                event.stopPropagation();
                event.preventDefault();
              }}
            />
          )}
        />
      </Table>
    </StyledTableWrapper>
  );
};
